import { animate, useMotionValue, useMotionValueEvent, useReducedMotion } from 'motion/react'
import { useCallback, useLayoutEffect, useRef } from 'react'

import { cn } from '../lib/utils'
import { PlateRole } from './plate-role-chip'

/** Any enum/string carrying a plate-role value — PlateRole.* or web's ComponentRole.*. */
type RoleValue = `${PlateRole}`

/** Wedges are laid down clockwise from 12 o'clock in this order. */
const ORDER: RoleValue[] = [PlateRole.Veggie, PlateRole.Protein, PlateRole.Starch, PlateRole.Sauce]

// Literal class strings, not `fill-role-${role}` — Tailwind's content scan only sees whole names.
// The /80 lets the ceramic read through the wedge; role tokens carry <alpha-value> so it compiles.
const ROLE_FILL: Record<RoleValue, string> = {
  veggie: 'fill-role-veggie/80',
  protein: 'fill-role-protein/80',
  starch: 'fill-role-starch/80',
  sauce: 'fill-role-sauce/80'
}

type Parts = Partial<Record<RoleValue, number>>

/** Four dots across three roles — the fullest plate the scale can express. */
const MAX_TOTAL = 12

// A plain ease-out, not a spring: overshoot would extrapolate past the target shape, and a plate
// that wobbles past the wedge you asked for reads as a glitch rather than as life.
const TRANSITION = { duration: 0.42, ease: [0.32, 0.72, 0, 1] } as const

export interface PlateProps {
  /** Parts per role — any non-negative integers; wedges are proportional to the total. */
  parts: Parts
  /** Diameter in px. Default 188 (the editor's preview size). */
  size?: number
  /**
   * Accessible name, already translated — this package stays i18n-agnostic. Omit ONLY where
   * adjacent visible text already names the shape (a card whose label is "Veggie-forward"), so the
   * plate is decorative there; anywhere it carries the meaning on its own, name it.
   */
  label?: string
  className?: string
}

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  const rad = ((angle - 90) * Math.PI) / 180
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
}

function wedgePath(c: number, r: number, from: number, to: number): string {
  const [x0, y0] = polar(c, c, r, from)
  const [x1, y1] = polar(c, c, r, to)
  const largeArc = to - from > 180 ? 1 : 0
  return `M ${c} ${c} L ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} Z`
}

/** A lone role has no neighbour to separate from, so it fills the well with no seam at 12 o'clock. */
const fullPath = (c: number, r: number): string => `M ${c - r} ${c} A ${r} ${r} 0 1 0 ${c + r} ${c} A ${r} ${r} 0 1 0 ${c - r} ${c} Z`

/** The empty well the food sits in — fixed, so the food has something to be a portion OF. */
const wellRadius = (size: number): number => size * 0.38

/**
 * How far the food reaches. AREA is proportional to the total, so six parts reads as twice the
 * food of three rather than four times it; a full plate reaches the well's edge exactly.
 */
function fillRadius(parts: Parts, size: number): number {
  const total = ORDER.reduce((sum, role) => sum + (parts[role] ?? 0), 0)
  return wellRadius(size) * Math.sqrt(Math.min(total, MAX_TOTAL) / MAX_TOTAL)
}

/** One `d` per role for a given set of parts. Empty string where the role holds nothing. */
function wedgePaths(parts: Parts, size: number): Record<RoleValue, string> {
  const c = size / 2
  const r = fillRadius(parts, size)
  const total = ORDER.reduce((sum, role) => sum + (parts[role] ?? 0), 0)
  const out = {} as Record<RoleValue, string>
  let angle = 0

  for (const role of ORDER) {
    const span = total > 0 ? ((parts[role] ?? 0) / total) * 360 : 0
    out[role] = span <= 0.01 ? '' : span >= 359.9 ? fullPath(c, r) : wedgePath(c, r, angle, angle + span)
    angle += span
  }
  return out
}

// Clamped: outside [0,1] this extrapolates to plates that were never asked for, and far enough out
// a role goes negative and the geometry stops meaning anything.
const lerp = (from: Parts, to: Parts, t: number): Parts => {
  const k = t < 0 ? 0 : t > 1 ? 1 : t
  return Object.fromEntries(ORDER.map((role) => [role, (from[role] ?? 0) + ((to[role] ?? 0) - (from[role] ?? 0)) * k]))
}

const samePlate = (a: Parts, b: Parts): boolean => ORDER.every((role) => (a[role] ?? 0) === (b[role] ?? 0))

/**
 * A ceramic plate seen from above: a fixed empty well holding food divided into wedges by role.
 *
 * `parts` say two things at once. Their ratio sets the wedge angles, and their total sets how far
 * the food reaches — so 1/1/1 and 4/4/4 are the same meal in different portions, and look it.
 *
 * The single home for "how Parsley draws a plate" — the shape the plate-shape editor tunes and
 * the eating-style tile reports. An all-zero plate renders an empty well.
 *
 * Re-shaping is animated: the parts are interpolated and the arcs redrawn each frame, so wedges
 * both swing to their new angles and grow or shrink with the total. Paths are written straight to
 * the DOM, so a plate mid-transition costs no React renders, and one whose parts never change
 * never animates.
 */
export function Plate({ parts, size = 188, label, className }: PlateProps) {
  const reduce = useReducedMotion() ?? false
  const c = size / 2
  const plateRadius = c - 3
  const well = wellRadius(size)
  // Thumbnails are a third the preview's size; a 3px seam there reads as a gap, not a separation.
  const seam = size > 120 ? 3 : 2

  const paths = useRef<Partial<Record<RoleValue, SVGPathElement | null>>>({})
  // Where the current transition started and where it's headed. Starting from the LIVE shape (not
  // the previous target) is what lets a tap mid-flight bend the motion instead of restarting it.
  const from = useRef<Parts>(parts)
  const to = useRef<Parts>(parts)
  const progress = useMotionValue(1)

  const draw = useCallback(
    (t: number) => {
      const next = wedgePaths(lerp(from.current, to.current, t), size)
      for (const role of ORDER) paths.current[role]?.setAttribute('d', next[role])
    },
    [size]
  )

  useLayoutEffect(() => {
    if (samePlate(to.current, parts)) return
    from.current = lerp(from.current, to.current, progress.get())
    to.current = parts
    progress.set(0)
    // React has already painted the target `d` from the render below; this runs before the browser
    // paints, so overwriting it here is what keeps the first frame from snapping.
    draw(0)
    if (reduce) {
      progress.set(1)
      return
    }
    // velocity 0 explicitly: the jump from 1 to 0 above reads as a large negative velocity, which
    // animate() would otherwise inherit and carry backwards before heading for the target.
    const controls = animate(progress, 1, { ...TRANSITION, velocity: 0 })
    return () => controls.stop()
  }, [parts, draw, progress, reduce])

  useMotionValueEvent(progress, 'change', draw)

  const initial = wedgePaths(parts, size)

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('drop-shadow-plate', className)}>
      <circle cx={c} cy={c} r={plateRadius} className="fill-card" />
      <circle cx={c} cy={c} r={plateRadius - 1} fill="none" strokeWidth="1" className="stroke-border/80" />
      {/* Rim detail — a second ring just outside the well, where a real plate's curve begins. */}
      <circle cx={c} cy={c} r={well + size * 0.045} fill="none" strokeWidth="1" className="stroke-border/55" />
      {/* The well is fixed and always drawn; the food is a portion of it, so a light plate shows a
          ring of empty china around a small mound. */}
      <circle cx={c} cy={c} r={well} className="fill-surface-inset" />
      {ORDER.map((role) => (
        <path
          key={role}
          ref={(el) => {
            paths.current[role] = el
          }}
          d={initial[role]}
          className={cn(ROLE_FILL[role], 'stroke-card')}
          strokeWidth={seam}
          strokeLinejoin="round"
        />
      ))}
    </svg>
  )
}
